import React, { useState, useEffect } from 'react';
import { Save, FlaskConical, Upload, Palette, Mail, MessageSquare, Users, Shield, Plus, Edit } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useConfig } from '../context/ConfigContext';

const FONTES = ['Roboto', 'Lato', 'Open Sans', 'Source Sans 3', 'Calibri', 'Arial', 'Georgia'];

const TEMAS = [
  {
    id: 'verde-floresta', nome: 'Verde Floresta', desc: 'Padrão — rural, natural',
    primaria: '#2D6A4F', secundaria: '#40916C', bg: '#F8F7F4', bgCard: '#FFFFFF', sidebar: '#1B4332', texto: '#1C1917', modo: 'light'
  },
  {
    id: 'azul-corporativo', nome: 'Azul Corporativo', desc: 'Profissional, tecnológico',
    primaria: '#1E40AF', secundaria: '#2563EB', bg: '#F0F4FF', bgCard: '#FFFFFF', sidebar: '#1E3A8A', texto: '#1E293B', modo: 'light'
  },
  {
    id: 'terracota', nome: 'Terracota & Bege', desc: 'Quente, fazenda tradicional',
    primaria: '#92400E', secundaria: '#B45309', bg: '#FFFBF5', bgCard: '#FFFFFF', sidebar: '#78350F', texto: '#1C1917', modo: 'light'
  },
  {
    id: 'roxo-moderno', nome: 'Roxo Moderno', desc: 'Sofisticado, tecnológico',
    primaria: '#6D28D9', secundaria: '#7C3AED', bg: '#FAF5FF', bgCard: '#FFFFFF', sidebar: '#4C1D95', texto: '#1E1B4B', modo: 'light'
  },
  {
    id: 'cinza-neutro', nome: 'Cinza Neutro', desc: 'Minimalista, corporativo',
    primaria: '#374151', secundaria: '#4B5563', bg: '#F9FAFB', bgCard: '#FFFFFF', sidebar: '#111827', texto: '#111827', modo: 'light'
  },
  {
    id: 'verde-escuro', nome: 'Dark Verde', desc: 'Tema escuro com acento verde',
    primaria: '#34D399', secundaria: '#6EE7B7', bg: '#0F172A', bgCard: '#1E293B', sidebar: '#064E3B', texto: '#F1F5F9', modo: 'dark'
  },
  {
    id: 'dark-azul', nome: 'Dark Azul', desc: 'Tema escuro profissional',
    primaria: '#60A5FA', secundaria: '#93C5FD', bg: '#0F172A', bgCard: '#1E293B', sidebar: '#1E3A8A', texto: '#F1F5F9', modo: 'dark'
  },
  {
    id: 'laranja-energia', nome: 'Laranja & Cinza', desc: 'Energético, impactante',
    primaria: '#EA580C', secundaria: '#F97316', bg: '#FFF7ED', bgCard: '#FFFFFF', sidebar: '#7C2D12', texto: '#1C1917', modo: 'light'
  },
  {
    id: 'teal-aqua', nome: 'Teal Aqua', desc: 'Fresco, moderno',
    primaria: '#0F766E', secundaria: '#0D9488', bg: '#F0FDFA', bgCard: '#FFFFFF', sidebar: '#134E4A', texto: '#1C1917', modo: 'light'
  },
  {
    id: 'rosa-vibrante', nome: 'Rosa & Vinho', desc: 'Feminino, vibrante',
    primaria: '#BE185D', secundaria: '#DB2777', bg: '#FFF0F6', bgCard: '#FFFFFF', sidebar: '#831843', texto: '#1C1917', modo: 'light'
  },
];

export default function Configuracoes() {
  const { loadConfig } = useConfig();
  const [cfg, setCfg] = useState({});
  const [tab, setTab] = useState('empresa');
  const [usuarios, setUsuarios] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editandoUser, setEditandoUser] = useState(null);
  const [userForm, setUserForm] = useState({ nome: '', email: '', senha: '', perfil_id: '', ativo: true });
  const [temaSelecionado, setTemaSelecionado] = useState('');

  useEffect(() => {
    api.get('/configuracoes').then(r => {
      setCfg(r.data);
      setTemaSelecionado(r.data.tema_id || '');
    });
  }, []);

  const carregarUsuarios = async () => {
    setLoadingUsuarios(true);
    try {
      const [u, p] = await Promise.all([api.get('/usuarios'), api.get('/usuarios/perfis')]);
      setUsuarios(u.data);
      setPerfis(p.data);
    } catch (e) {
      toast.error('Erro ao carregar usuários: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoadingUsuarios(false);
    }
  };

  useEffect(() => {
    if (tab === 'usuarios' || tab === 'perfis') carregarUsuarios();
  }, [tab]);

  const salvar = async () => {
    try {
      await api.put('/configuracoes', cfg);
      await loadConfig();
      toast.success('Configurações salvas!');
    } catch (e) { toast.error('Erro ao salvar'); }
  };

  const aplicarTema = (tema) => {
    setTemaSelecionado(tema.id);
    setCfg(p => ({
      ...p,
      tema_id: tema.id,
      tema: tema.modo,
      cor_primaria: tema.primaria,
      cor_secundaria: tema.secundaria,
    }));
    // Preview imediato
    document.documentElement.style.setProperty('--color-primary', tema.primaria);
    document.documentElement.style.setProperty('--color-secondary', tema.secundaria);
    document.body.setAttribute('data-theme', tema.modo);
    if (tema.modo === 'dark') {
      document.documentElement.style.setProperty('--bg-main', tema.bg);
      document.documentElement.style.setProperty('--bg-card', tema.bgCard);
    } else {
      document.documentElement.style.removeProperty('--bg-main');
      document.documentElement.style.removeProperty('--bg-card');
    }
    toast.success(`Tema "${tema.nome}" aplicado! Salve para persistir.`);
  };

  const testarEmail = async () => {
    try { await api.post('/configuracoes/testar-email'); toast.success('E-mail de teste enviado!'); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const testarTelegram = async () => {
    try { await api.post('/configuracoes/testar-telegram'); toast.success('Mensagem Telegram enviada!'); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const abrirUserModal = (user = null) => {
    setEditandoUser(user);
    setUserForm(user
      ? { nome: user.nome, email: user.email, senha: '', nova_senha: '', perfil_id: user.perfil_id || '', ativo: user.ativo }
      : { nome: '', email: '', senha: '', perfil_id: '', ativo: true });
    setShowUserModal(true);
  };

  const salvarUsuario = async () => {
    try {
      if (editandoUser) {
        const payload = { ...userForm };
        if (!payload.nova_senha) delete payload.nova_senha;
        await api.put(`/usuarios/${editandoUser.id}`, payload);
        toast.success('Usuário atualizado!');
      } else {
        await api.post('/usuarios', userForm);
        toast.success('Usuário criado!');
      }
      setShowUserModal(false);
      carregarUsuarios();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const set = (k, v) => setCfg(p => ({ ...p, [k]: v }));

  const tabs = [
    { id: 'empresa', label: 'Empresa', icon: Save },
    { id: 'aparencia', label: 'Aparência', icon: Palette },
    { id: 'email', label: 'E-mail', icon: Mail },
    { id: 'telegram', label: 'Telegram', icon: MessageSquare },
    { id: 'usuarios', label: 'Usuários', icon: Users },
    { id: 'perfis', label: 'Perfis', icon: Shield },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><h1>Configurações</h1><p>Central de configurações do sistema</p></div>
        {tab !== 'usuarios' && tab !== 'perfis' && (
          <button className="btn btn-primary" onClick={salvar}><Save size={16} /> Salvar</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} className={`btn ${tab === t.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t.id)}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── EMPRESA ── */}
      {tab === 'empresa' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 style={{ marginBottom: 20 }}>Dados da Empresa</h3>
          <div className="form-group">
            <label>Nome da Empresa / Fazenda</label>
            <input value={cfg.empresa_nome || ''} onChange={e => set('empresa_nome', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Logotipo</label>
            {cfg.empresa_logo && <img src={cfg.empresa_logo} alt="Logo" style={{ height: 56, marginBottom: 8, display: 'block', borderRadius: 8, border: '1px solid var(--border-color)', padding: 4, background: 'white' }} />}
            <input type="file" accept="image/*" onChange={async e => {
              const fd = new FormData(); fd.append('logo', e.target.files[0]);
              try { const { data } = await api.post('/configuracoes/logo', fd); set('empresa_logo', data.logo); toast.success('Logo atualizada!'); }
              catch { toast.error('Erro ao enviar logo'); }
            }} />
          </div>
          <div className="form-row">
            <div className="form-group"><label>GMD Mínimo Alerta — descarte (kg/dia)</label><input type="number" step="0.01" value={cfg.gmd_minimo_alerta || 0.1} onChange={e => set('gmd_minimo_alerta', e.target.value)} /></div>
            <div className="form-group"><label>Idade Desmame (dias)</label><input type="number" value={cfg.idade_desmame_dias || 60} onChange={e => set('idade_desmame_dias', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>GMD Verde a partir de (g/dia)</label><input type="number" step="1" value={cfg.gmd_verde_min_g ?? 150} onChange={e => set('gmd_verde_min_g', e.target.value)} /></div>
            <div className="form-group"><label>GMD Amarelo a partir de (g/dia)</label><input type="number" step="1" value={cfg.gmd_amarelo_min_g ?? 100} onChange={e => set('gmd_amarelo_min_g', e.target.value)} /></div>
          </div>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginTop:-8, marginBottom:12 }}>
            Critérios da legenda de GMD em Pesagens e no Relatório GMD: abaixo do "Amarelo" fica vermelho.
          </p>
          <div className="form-row">
            <div className="form-group"><label>Duração Gestação (dias)</label><input type="number" value={cfg.gestacao_dias || 147} onChange={e => set('gestacao_dias', e.target.value)} /></div>
            <div className="form-group"><label>Alerta Parto (dias antes)</label><input type="number" value={cfg.alerta_parto_dias || 15} onChange={e => set('alerta_parto_dias', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Prazo máximo sem diagnóstico de prenhez (meses)</label>
              <input type="number" min="1" max="24" value={cfg.prazo_matriz_sem_prenhez || 6} onChange={e => set('prazo_matriz_sem_prenhez', e.target.value)} />
              <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:4 }}>Matrizes sem prenhez diagnosticada após o último parto além deste prazo serão marcadas no Descarte Técnico</div>
            </div>
          </div>
        </div>
      )}

      {/* ── APARÊNCIA ── */}
      {tab === 'aparencia' && (
        <div>
          <h3 style={{ marginBottom: 16 }}>Temas Predefinidos</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 28 }}>
            {TEMAS.map(tema => (
              <div key={tema.id} onClick={() => aplicarTema(tema)} style={{
                border: `2px solid ${temaSelecionado === tema.id ? tema.primaria : 'var(--border-color)'}`,
                borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                boxShadow: temaSelecionado === tema.id ? `0 0 0 3px ${tema.primaria}30` : 'var(--shadow-sm)',
                transition: 'all 0.2s',
              }}>
                {/* Preview miniatura */}
                <div style={{ height: 70, background: tema.bg, position: 'relative', display: 'flex' }}>
                  {/* Sidebar mini */}
                  <div style={{ width: 24, background: tema.sidebar, height: '100%', display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 4px' }}>
                    {[20, 16, 16, 14, 14].map((w, i) => <div key={i} style={{ height: 3, width: w, borderRadius: 2, background: 'rgba(255,255,255,0.4)' }} />)}
                  </div>
                  {/* Content mini */}
                  <div style={{ flex: 1, padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[1, 2, 3].map(i => <div key={i} style={{ flex: 1, height: 18, background: tema.bgCard, border: `1px solid ${tema.primaria}30`, borderRadius: 4, borderTop: `3px solid ${tema.primaria}` }} />)}
                    </div>
                    <div style={{ height: 12, background: tema.bgCard, borderRadius: 4, border: `1px solid ${tema.primaria}20` }} />
                    <div style={{ display: 'flex', gap: 4 }}>
                      <div style={{ width: 28, height: 12, background: tema.primaria, borderRadius: 3 }} />
                      <div style={{ width: 20, height: 12, background: `${tema.primaria}40`, borderRadius: 3 }} />
                    </div>
                  </div>
                </div>
                {/* Info */}
                <div style={{ padding: '10px 12px', background: 'var(--bg-card)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{tema.nome}</span>
                    {temaSelecionado === tema.id && (
                      <span style={{ background: tema.primaria, color: 'white', fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700 }}>ATIVO</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{tema.desc}</div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: tema.primaria, border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: tema.secundaria, border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: tema.sidebar, border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4, lineHeight: '18px' }}>{tema.modo === 'dark' ? '🌙 Escuro' : '☀️ Claro'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ maxWidth: 600 }}>
            <h3 style={{ marginBottom: 16 }}>Personalização Manual</h3>
            <div className="form-group"><label>Fonte</label>
              <select value={cfg.fonte || 'Roboto'} onChange={e => { set('fonte', e.target.value); document.body.style.fontFamily = `'${e.target.value}', sans-serif`; }}>
                {FONTES.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Cor Primária</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={cfg.cor_primaria || '#2D6A4F'} onChange={e => { set('cor_primaria', e.target.value); document.documentElement.style.setProperty('--color-primary', e.target.value); }} style={{ width: 50, height: 38, padding: 2, cursor: 'pointer' }} />
                  <input value={cfg.cor_primaria || '#2D6A4F'} onChange={e => { set('cor_primaria', e.target.value); document.documentElement.style.setProperty('--color-primary', e.target.value); }} style={{ flex: 1 }} />
                </div>
              </div>
              <div className="form-group"><label>Cor Secundária</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={cfg.cor_secundaria || '#40916C'} onChange={e => { set('cor_secundaria', e.target.value); document.documentElement.style.setProperty('--color-secondary', e.target.value); }} style={{ width: 50, height: 38, padding: 2, cursor: 'pointer' }} />
                  <input value={cfg.cor_secundaria || '#40916C'} onChange={e => { set('cor_secundaria', e.target.value); document.documentElement.style.setProperty('--color-secondary', e.target.value); }} style={{ flex: 1 }} />
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ maxWidth: 600, marginTop: 20 }}>
            <h3 style={{ marginBottom: 16 }}>Tamanho da Fonte</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Ajuste o tamanho do texto da aplicação. Útil para uso em tablets e celulares.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {[
                { value: 'pequena', label: 'Pequena', preview: '13px', desc: '90%', scale: '0.9' },
                { value: 'normal', label: 'Normal', preview: '14px', desc: '100%', scale: '1' },
                { value: 'grande', label: 'Grande', preview: '16px', desc: '115%', scale: '1.15' },
                { value: 'extra_grande', label: 'Extra Grande', preview: '18px', desc: '130%', scale: '1.3' },
              ].map(opt => (
                <div
                  key={opt.value}
                  onClick={() => {
                    set('tamanho_fonte', opt.value);
                    document.documentElement.style.setProperty('--font-scale', opt.scale);
                    toast.success(`Fonte "${opt.label}" aplicada! Salve para persistir.`);
                  }}
                  style={{
                    padding: '14px 12px',
                    border: `2px solid ${(cfg.tamanho_fonte || 'normal') === opt.value ? 'var(--color-primary)' : 'var(--border-color)'}`,
                    borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                    background: (cfg.tamanho_fonte || 'normal') === opt.value ? 'rgba(45,106,79,0.08)' : 'var(--bg-card)',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: opt.preview, fontWeight: 700, marginBottom: 4 }}>Aa</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── E-MAIL ── */}
      {tab === 'email' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 style={{ marginBottom: 20 }}>Servidor de E-mail (SMTP)</h3>
          <div className="form-row">
            <div className="form-group"><label>Host SMTP</label><input value={cfg.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" /></div>
            <div className="form-group"><label>Porta</label><input type="number" value={cfg.smtp_port || 587} onChange={e => set('smtp_port', e.target.value)} /></div>
          </div>
          <div className="form-group"><label>Usuário</label><input value={cfg.smtp_user || ''} onChange={e => set('smtp_user', e.target.value)} placeholder="seu@email.com" /></div>
          <div className="form-group"><label>Senha</label><input type="password" value={cfg.smtp_pass || ''} onChange={e => set('smtp_pass', e.target.value)} placeholder="••••••••" /></div>
          <div className="form-group"><label>E-mail Remetente (From)</label><input value={cfg.smtp_from || ''} onChange={e => set('smtp_from', e.target.value)} placeholder="OvinoGest <noreply@fazenda.com>" /></div>
          <div style={{ paddingTop: 8 }}>
            <button className="btn btn-secondary" onClick={testarEmail}><FlaskConical size={14} /> Testar Conexão</button>
          </div>
        </div>
      )}

      {/* ── TELEGRAM ── */}
      {tab === 'telegram' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <h3 style={{ marginBottom: 8 }}>Telegram Bot</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>Receba alertas e notificações diretamente no Telegram.</p>
          <div className="form-group"><label>Token do Bot</label><input value={cfg.telegram_token || ''} onChange={e => set('telegram_token', e.target.value)} placeholder="1234567890:AAFxxxxxxxxxxxxxxxx" /></div>
          <div className="form-group"><label>Chat ID do grupo/canal</label><input value={cfg.telegram_chat_id || ''} onChange={e => set('telegram_chat_id', e.target.value)} placeholder="-1001234567890" /></div>
          <div style={{ padding: '12px 16px', background: 'var(--bg-main)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            💡 <strong>Como configurar:</strong> 1) Crie um bot com @BotFather no Telegram → 2) Copie o token gerado → 3) Adicione o bot ao grupo desejado → 4) Use @userinfobot para obter o Chat ID do grupo
          </div>
          <button className="btn btn-secondary" onClick={testarTelegram}><FlaskConical size={14} /> Testar Conexão</button>
        </div>
      )}

      {/* ── USUÁRIOS ── */}
      {tab === 'usuarios' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => abrirUserModal()}><Plus size={16} /> Novo Usuário</button>
          </div>
          {loadingUsuarios ? (
            <div className="card"><div className="empty-state"><p>Carregando usuários...</p></div></div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table>
                <thead><tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>Último Acesso</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {usuarios.length === 0 ? (
                    <tr><td colSpan={6}><div className="empty-state"><p>Nenhum usuário cadastrado</p></div></td></tr>
                  ) : usuarios.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.nome}</strong></td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{u.email}</td>
                      <td><span className="badge badge-blue">{u.perfil_nome || '—'}</span></td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleDateString('pt-BR') : 'Nunca'}</td>
                      <td><span className={`badge ${u.ativo ? 'badge-green' : 'badge-gray'}`}>{u.ativo ? 'Ativo' : 'Inativo'}</span></td>
                      <td><button className="btn btn-secondary btn-sm" onClick={() => abrirUserModal(u)}><Edit size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PERFIS ── */}
      {tab === 'perfis' && (
        <div>
          {loadingUsuarios ? (
            <div className="card"><div className="empty-state"><p>Carregando perfis...</p></div></div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table>
                <thead><tr><th>Perfil</th><th>Descrição</th><th>Permissões</th><th>Status</th></tr></thead>
                <tbody>
                  {perfis.length === 0 ? (
                    <tr><td colSpan={4}><div className="empty-state"><p>Nenhum perfil encontrado</p></div></td></tr>
                  ) : perfis.map(p => (
                    <tr key={p.id}>
                      <td><strong>{p.nome}</strong></td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{p.descricao}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {p.permissoes?.all ? <span className="badge badge-green">Acesso Total</span>
                          : <span>{Object.keys(p.permissoes || {}).join(', ')}</span>}
                      </td>
                      <td><span className={`badge ${p.ativo ? 'badge-green' : 'badge-gray'}`}>{p.ativo ? 'Ativo' : 'Inativo'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL USUÁRIO ── */}
      {showUserModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowUserModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editandoUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowUserModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label>Nome completo *</label><input value={userForm.nome} onChange={e => setUserForm(p => ({ ...p, nome: e.target.value }))} /></div>
              <div className="form-group"><label>E-mail *</label><input type="email" value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} /></div>
              {!editandoUser && (
                <div className="form-group"><label>Senha *</label><input type="password" value={userForm.senha} onChange={e => setUserForm(p => ({ ...p, senha: e.target.value }))} placeholder="Mínimo 8 caracteres" /></div>
              )}
              {editandoUser && (
                <div className="form-group"><label>Nova Senha (deixe em branco para não alterar)</label><input type="password" value={userForm.nova_senha || ''} onChange={e => setUserForm(p => ({ ...p, nova_senha: e.target.value }))} placeholder="Nova senha..." /></div>
              )}
              <div className="form-group"><label>Perfil de Acesso *</label>
                <select value={userForm.perfil_id} onChange={e => setUserForm(p => ({ ...p, perfil_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {perfis.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
              {editandoUser && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={userForm.ativo} onChange={e => setUserForm(p => ({ ...p, ativo: e.target.checked }))} style={{ width: 'auto' }} />
                    Usuário ativo
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarUsuario} disabled={!userForm.nome || !userForm.email || (!editandoUser && !userForm.senha)}>
                {editandoUser ? 'Atualizar' : 'Criar Usuário'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
